import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from '@mui/material';
import apiCall from "../../../../API/apiService"; // Ensure this is imported
import apiEndpoints from "../../../../API/apiEndpoints"; // Ensure this is imported

const PromptForm = ({ open, onClose, jobOpeningId, isUpdate }) => {
	const [formData, setFormData] = useState({
		initial_prompt: '',
		greeting_prompt: '',
		ending_prompt: '',
		force_next_question_prompt: '',
		next_question_prompt: '',
		coding_question_prompt: '',
		confirm_answer_given_prompt: '',
		follow_up_prompt: '',
		confirm_action_prompt: '',
		take_get_question_action_prompt: '',
		take_reschedule_interview_action_prompt: '',
		evaluate_answer_prompt: '',
		evaluate_transcript_prompt: '',
		user_idle_prompt: '',
		user_idle_timeout: '',
	});
	const [error, setError] = useState(null);
	const [fieldErrors, setFieldErrors] = useState({});

	// Add validation check
	const isFormValid = () => {
		return Object.entries(formData).every(([key, value]) => {
			if (key === 'user_idle_prompt' || key === 'user_idle_timeout') {
				return true;
			}
			return value.trim() !== '';
		});
	};

	// Fetch existing prompts when updating
	useEffect(() => {
		if (open && isUpdate && jobOpeningId) {
			fetchExistingPrompts();
		} else if (open) {
			// Reset form for create
			setFormData({
				initial_prompt: '',
				greeting_prompt: '',
				ending_prompt: '',
				force_next_question_prompt: '',
				next_question_prompt: '',
				coding_question_prompt: '',
				follow_up_prompt: '',
				confirm_action_prompt: '',
				take_get_question_action_prompt: '',
				take_reschedule_interview_action_prompt: '',
				evaluate_answer_prompt: '',
				evaluate_transcript_prompt: '',
				user_idle_prompt: '',
				user_idle_timeout: '',
			});
			setError(null); // Reset error when opening form
		}
	}, [open, jobOpeningId, isUpdate]);

	const fetchExistingPrompts = async () => {
		try {
			const response = await apiCall("get", `${apiEndpoints.PROMPTS}/${jobOpeningId}`);
			if (!response || Object.keys(response).length === 0) {
				setError("This job opening doesn't have a prompt created yet. Please create a prompt first.");
				return;
			}
			setFormData({
				initial_prompt: response.initial_prompt || '',
				greeting_prompt: response.greeting_prompt || '',
				ending_prompt: response.ending_prompt || '',
				force_next_question_prompt: response.force_next_question_prompt || '',
				next_question_prompt: response.next_question_prompt || '',
				coding_question_prompt: response.coding_question_prompt || '',
				follow_up_prompt: response.follow_up_prompt || '',
				confirm_action_prompt: response.confirm_action_prompt || '',
				take_get_question_action_prompt: response.take_get_question_action_prompt || '',
				take_reschedule_interview_action_prompt: response.take_reschedule_interview_action_prompt || '',
				evaluate_answer_prompt: response.evaluate_answer_prompt || '',
				evaluate_transcript_prompt: response.evaluate_transcript_prompt || '',
				user_idle_prompt: response.user_idle_prompt || '',
				user_idle_timeout: response.user_idle_timeout || 0,
			});
			setError(null);
		} catch (error) {
			console.error('Error fetching prompts:', error);
			setError("This job opening doesn't have a prompt created yet. Please create a prompt first.");
		}
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
		
		// Clear field error when user starts typing
		if (fieldErrors[name]) {
			setFieldErrors(prev => ({ ...prev, [name]: '' }));
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		
		// Validate all required fields before submission
		const errors = {};
		Object.entries(formData).forEach(([key, value]) => {
			if (key === 'user_idle_prompt' || key === 'user_idle_timeout') {
				return;
			}
			if (!value.trim()) {
				errors[key] = 'This field is required';
			}
		});

		if (Object.keys(errors).length > 0) {
			setFieldErrors(errors);
			return;
		}

		const payload = {
			...formData,
			jobopening_id: jobOpeningId,
		};

		try {
			const response = await apiCall(
				isUpdate ? "put" : "post",
				isUpdate ? `${apiEndpoints.PROMPTS}/${jobOpeningId}` : apiEndpoints.PROMPTS,
				payload
			);
			console.log(`Prompt ${isUpdate ? 'updated' : 'created'} successfully:`, response);
			onClose();
		} catch (error) {
			console.error(`Error ${isUpdate ? 'updating' : 'creating'} prompt:`, error);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>{isUpdate ? 'Update Prompt' : 'Create Prompt'}</DialogTitle>
			<DialogContent>
				<form onSubmit={handleSubmit}>
					<TextField
						autoFocus
						margin="dense"
						name="initial_prompt"
						label="Initial Prompt"
						type="text"
						fullWidth
						
						rows={4}
						value={formData.initial_prompt}
						onChange={handleChange}
						required
					/>
					<TextField
						margin="dense"
						name="greeting_prompt"
						label="Greeting Prompt"
						type="text"
						fullWidth
						
						rows={4}
						value={formData.greeting_prompt}
						onChange={handleChange}
						required
					/>
					<TextField
						margin="dense"
						name="ending_prompt"
						label="Ending Prompt"
						type="text"
						fullWidth
						
						rows={4}
						value={formData.ending_prompt}
						onChange={handleChange}
						required
					/>
					<TextField
						margin="dense"
						name="force_next_question_prompt"
						label="Force Next Question Prompt"
						type="text"
						fullWidth
						
						rows={4}
						value={formData.force_next_question_prompt}
						onChange={handleChange}
						required
					/>
					<TextField
						margin="dense"
						name="next_question_prompt"
						label="Next Question Prompt"
						type="text"
						fullWidth
						
						rows={4}
						value={formData.next_question_prompt}
						onChange={handleChange}
						required
					/>
					<TextField
						margin="dense"
						name="coding_question_prompt"
						label="Coding Question Prompt"
						type="text"
						fullWidth
						
						rows={4}
						value={formData.coding_question_prompt}
						onChange={handleChange}
						required
					/>
					
					<TextField
						margin="dense"
						name="follow_up_prompt"
						label="Follow Up Prompt"
						type="text"
						fullWidth
						
						rows={4}
						value={formData.follow_up_prompt}
						onChange={handleChange}
						required
					/>
					<TextField
						margin="dense"
						name="confirm_action_prompt"
						label="Confirm Action Prompt"
						type="text"
						fullWidth
						
						rows={4}
						value={formData.confirm_action_prompt}
						onChange={handleChange}
						required
					/>
					<TextField
						margin="dense"
						name="take_get_question_action_prompt"
						label="Take Get Question Action Prompt"
						type="text"
						fullWidth
					
						rows={4}
						value={formData.take_get_question_action_prompt}
						onChange={handleChange}
						required
						
						
					/>
					<TextField
						margin="dense"
						name="take_reschedule_interview_action_prompt"
						label="Take Reschedule Interview Action Prompt"
						type="text"
						fullWidth
						
						rows={4}
						value={formData.take_reschedule_interview_action_prompt}
						onChange={handleChange}
						required
					/>
					<TextField
						margin="dense"
						name="evaluate_answer_prompt"
						label="Evaluate Answer Prompt"
						type="text"
						fullWidth
						
						rows={4}
						value={formData.evaluate_answer_prompt}
						onChange={handleChange}
						required
					/>
					<TextField
						margin="dense"
						name="evaluate_transcript_prompt"
						label="Evaluate Transcript Prompt"
						type="text"
						fullWidth
						
						rows={4}
						value={formData.evaluate_transcript_prompt}
						onChange={handleChange}
						required
						
						
					/>
					<TextField
						margin="dense"
						name="user_idle_prompt"
						label="User Idle Prompt "
						type="text"
						fullWidth
						
						rows={4}
						value={formData.user_idle_prompt}
						onChange={handleChange}
						required
					/>
					<TextField
						margin="dense"
						name="user_idle_timeout"
						label="User Idle Timeout in seconds "
						type="number"
						fullWidth
						value={formData.user_idle_timeout}
						onChange={handleChange}
						inputProps={{ min: 1 }}
						error={fieldErrors.user_idle_timeout ? true : false}
						helperText={fieldErrors.user_idle_timeout}
						required
					/>
				</form>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="primary">
					{error ? 'Close' : 'Cancel'}
				</Button>
				{!error && (
					<Button 
						type="submit" 
						color="primary" 
						onClick={handleSubmit}
						disabled={!isFormValid()}
					>
						{isUpdate ? 'Update' : 'Submit'}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default PromptForm;
